import { SectionHeading } from "./section-heading";
import { ReportTable } from "./report-table/report-table";
import {
  OVERVIEW_SECTION,
  ITEM_SECTIONS,
  tallyItems,
  VerificationSectionId,
} from "./verification-sections";
import { renderWorkspaceRunActivity } from "../../psv-run-activity";
import { ITEMS_PENDING_MESSAGE } from "@/features/credentials/components/primary-source-runs/run-report-dialog/report-items";
import type { ReportReview } from "@/features/credentials/components/primary-source-runs/run-report-dialog/use-report-review";
import {
  isRunActive,
  type ReportItem,
  type RunReport,
  type RunScope,
} from "@/features/credentials/components/primary-source-runs/types";
import { RunFindingSummary } from "@/features/credentials/components/primary-source-runs/run-summary";

const KEYBOARD_HINTS = "J/K navigate · Enter expand · A accept · C concern";
const OVERVIEW_KEYBOARD_HINTS =
  "J/K navigate · Enter open · A accept · C concern";

/** The running activity line and, until sources finish, why items cannot be
 * decided yet. */
function RunActivity({ data }: { data: RunReport }) {
  if (!isRunActive(data.run)) {
    return null;
  }
  const activity = renderWorkspaceRunActivity({
    run: data.run,
    summary: data.summary,
    checks: data.checks,
    itemSummary: data.itemSummary,
  });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {activity?.badge}
        {!activity && <RunFindingSummary summary={data.summary} />}
      </div>
      {activity?.details}
    </div>
  );
}

/** One section of a run's report: its heading, review controls and items. */
export function ReportSectionView({
  data,
  scope,
  sectionId,
  items,
  canEdit,
  review,
  focusKey,
  onOpenItem,
  onFocused,
}: {
  data: RunReport;
  scope: RunScope;
  sectionId: VerificationSectionId;
  items: ReportItem[];
  canEdit: boolean;
  review: ReportReview;
  focusKey: string | null;
  /** Opens an overview row's item in its own section. */
  onOpenItem: (item: ReportItem) => void;
  onFocused: () => void;
}) {
  const isOverview = sectionId === VerificationSectionId.Overview;
  const section = isOverview
    ? OVERVIEW_SECTION
    : (ITEM_SECTIONS.find((entry) => entry.id === sectionId) ??
      OVERVIEW_SECTION);
  // What the automated sources returned, worded so it is not read as the
  // staff review count the summary strip shows ("Items reviewed").
  const description = isOverview
    ? `${data.summary.collectedChecks} of ${data.summary.totalChecks} sources returned a result`
    : undefined;
  const collecting = data.items.some((item) => item.decision === null);
  return (
    <div className="space-y-4">
      <SectionHeading
        section={section}
        description={description}
        tally={tallyItems(items)}
      />
      <RunActivity data={data} />
      {collecting && (
        <p role="status" className="text-sm text-muted-foreground">
          {ITEMS_PENDING_MESSAGE}
        </p>
      )}
      {items.length > 0 && (
        <ReportTable
          items={items}
          data={data}
          scope={scope}
          canEdit={canEdit}
          review={review}
          focusKey={focusKey}
          onOpen={isOverview ? onOpenItem : undefined}
          onFocused={onFocused}
        />
      )}
      {data.items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No applicable source results are recorded for this run.
        </p>
      )}
      {canEdit && items.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {isOverview ? OVERVIEW_KEYBOARD_HINTS : KEYBOARD_HINTS}
        </p>
      )}
    </div>
  );
}
